/**
 * mandate-registration-inheritance.ts - a new policy of a registered customer (Stefan P5 V2.0 steps 29-30,
 * Kap. 7.14): it follows its insurer's registration (Vererbung) or, at an insurer without one, the stored
 * Brokermandat is reported automatically (automatische Übertragung). The decision, the events and the tasks.
 *
 * Pure data and pure functions only, shared by api and web.
 */

import type { MandateInsurerNotificationStatus } from './versicherer-benachrichtigung';

/** What the policy hook does for one policy. */
export const REGISTRATION_INHERITANCE_DECISION = {
  /** The insurer confirmed the registration: link the policy, eligibility from max(inception, Wirkungsdatum). */
  INHERIT: 'inherit',
  /** The registration is open (planned, sent, reminded, in Klärung): the policy follows it. */
  FOLLOW_OPEN: 'follow_open',
  /** The registration waits because its policies were in Abklärung: follow it and plan it again. */
  REEVALUATE_DEFERRED: 'reevaluate_deferred',
  /** The insurer rejected the registration: the policy reads «nicht courtageberechtigt», a broker is told. */
  LINK_REJECTED: 'link_rejected',
  /** No registration at the insurer (or a closed one): report the Brokermandat there. */
  TRANSFER: 'transfer',
  NONE: 'none',
} as const;
export type RegistrationInheritanceDecision = (typeof REGISTRATION_INHERITANCE_DECISION)[keyof typeof REGISTRATION_INHERITANCE_DECISION];

/** Why the hook does nothing. */
export type InheritanceNoneReason = 'kunde_nicht_aktiv' | 'kein_brokermandat' | 'police_nicht_aktiv' | 'ohne_versicherer' | 'ausgenommen';

/** The policy statuses a registration applies to (a draft waits for its activation). */
export const INHERITING_POLICY_STATUSES: readonly string[] = ['active', 'in_mutation', 'renewal_pending'];

export interface InheritanceInput {
  /** customer_state.status of the policy's holder (null: no row). */
  customerStatus: string | null;
  /**
   * The holder's newest Brokermandat that is signed (null: none). A closed process does not matter:
   * steps 29-30 stay active after the Prozessabschluss (Kap. 7, OQ-S7-1).
   */
  brokerMandate: { status: string; processClosedAt?: string | null } | null;
  policy: { status: string; carrierUid: string | null; exceptionReason: string | null };
  /** The newest registration row of that mandate at the policy's insurer, any status (null: none). */
  registration: { status: MandateInsurerNotificationStatus; exceptionCodes?: readonly string[] } | null;
}

export interface InheritanceOutcome {
  decision: RegistrationInheritanceDecision;
  reason?: InheritanceNoneReason;
}

function assertNever(x: never): never {
  throw new Error(`unhandled registration status ${String(x)}`);
}

/**
 * The rule table of Kap. 7.14 (slice spec §2.1): only for a customer at aktiv with a signed Brokermandat
 * and an active policy with an insurer UID. Exhaustive over the registration status. Pure.
 */
export function decideInheritance(input: InheritanceInput): InheritanceOutcome {
  if (input.customerStatus !== 'aktiv') return { decision: 'none', reason: 'kunde_nicht_aktiv' };
  if (input.brokerMandate === null || input.brokerMandate.status !== 'signed') return { decision: 'none', reason: 'kein_brokermandat' };
  if (!INHERITING_POLICY_STATUSES.includes(input.policy.status)) return { decision: 'none', reason: 'police_nicht_aktiv' };
  if (input.policy.carrierUid === null || input.policy.carrierUid.trim() === '') return { decision: 'none', reason: 'ohne_versicherer' };
  if (input.policy.exceptionReason !== null) return { decision: 'none', reason: 'ausgenommen' };
  const reg = input.registration;
  if (reg === null) return { decision: 'transfer' };
  const s = reg.status;
  switch (s) {
    case 'bestaetigt':
      return { decision: 'inherit' };
    case 'sending':
    case 'zur_freigabe':
    case 'benachrichtigt':
    case 'erinnert':
    case 'klaerung':
    case 'rueckfrage':
      return { decision: 'follow_open' };
    case 'zurueckgestellt':
      // A planned row waiting for its job goes out anyway; only a deferred one is planned again.
      return { decision: (reg.exceptionCodes ?? []).includes('send_pending') ? 'follow_open' : 'reevaluate_deferred' };
    case 'abgelehnt':
      return { decision: 'link_rejected' };
    case 'manuell_geschlossen':
    // a de-registered insurer (an ended mandate the customer took back) gets a new Meldung
    case 'ausgetragen':
    case 'informiert':
      return { decision: 'transfer' };
    default:
      return assertNever(s);
  }
}

/** The activity events of the hook (Kap. 12; meta = ids and codes only). */
export const REGISTRATION_INHERITANCE_EVENTS = {
  INHERITED: 'policy.registration_inherited',
  FOLLOWED: 'policy.registration_followed',
  REJECTED_CARRIER: 'policy.registration_rejected_carrier',
  TRANSFER_PENDING: 'policy.registration_transfer_pending',
  NO_CARRIER: 'policy.registration_no_carrier',
} as const;

/** The tasks of the hook (process mandate_registration; meta.kind is the stable key). */
export const REGISTRATION_INHERITANCE_TASK = {
  /** The hook failed (a bug, a lock timeout): the policy stands, a broker checks its registration. */
  VERERBUNG_PRUEFEN: { kind: 'vererbung_pruefen', title: 'Vererbung prüfen' },
  /** A new policy at an insurer that rejected the registration (Kap. 8 row 22). */
  NEUE_POLICE_ABGELEHNT: { kind: 'neue_police_abgelehnte_eintragung', title: 'Neue Police bei abgelehnter Eintragung' },
} as const;

/** GET /policies/:id/registration-preview: what activating the policy would do. */
export interface PolicyRegistrationPreviewDto {
  decision: RegistrationInheritanceDecision;
  /** The activation reports the Brokermandat to the insurer (an exception rule may still hold it). */
  meldung: boolean;
  /** The Meldung waits for a broker's release: the caller lacks communication.send. */
  freigabe: boolean;
  /** The tenant's name of the insurer, when known. */
  carrier_name: string | null;
}

/** POST /tasks/:taskId/vererbung/erneut-pruefen */
export interface VererbungRetryResultDto {
  decision: RegistrationInheritanceDecision;
  closed_task_ids: string[];
}

/**
 * The one Altbestand rule of the Brokermandat registration (S6's cutoff): a policy created, or a
 * Brokermandat signed, before the tenant's cutoff never reports the Brokermandat to an insurer by itself; a
 * broker releases it. No cutoff (a tenant not yet scanned): nothing is Altbestand. Used by the policy hook,
 * the Meldeliste (finalise and transfer), the activation preview; the SQL of the sweep, the Altbestand scan
 * and the job's refresh is the same comparison (`policyAltbestandSql`). Pure.
 */
export function isRegistrationAltbestand(input: { cutoff: string | null; policyCreatedAt?: string | null; mandateSignedAt?: string | null }): boolean {
  if (input.cutoff === null) return false;
  const cutoff = Date.parse(input.cutoff);
  const before = (at: string | null | undefined): boolean => at !== null && at !== undefined && Date.parse(at) < cutoff;
  return before(input.policyCreatedAt) || before(input.mandateSignedAt);
}
